/**
 * Geometry for the live US dot-matrix map behind the Convergence hero.
 *
 * The lattice, the hub positions and the routes are all derived here so the
 * canvas component only has to animate them.
 */

export type Point = readonly [number, number];

/**
 * Continental US outline as flattened lon/lat pairs, clockwise from Cape
 * Flattery. Simplified to the resolution the dot lattice can actually show:
 * one lattice cell spans roughly 200km, so finer coastline detail is invisible.
 */
const OUTLINE_FLAT = [
  -124.7, 48.4, -123.0, 49.0, -117.0, 49.0, -110.0, 49.0, -104.0, 49.0, -97.2,
  49.0, -95.15, 49.4, -95.15, 48.9, -93.5, 48.6, -91.5, 48.1, -89.5, 48.0,
  -90.9, 46.6, -88.0, 46.5, -85.0, 46.75, -84.6, 46.0, -83.0, 46.0, -82.4, 45.0,
  -82.5, 43.0, -83.1, 42.0, -82.7, 41.7, -80.5, 42.3, -79.0, 42.8, -77.0, 43.3,
  -76.5, 44.1, -74.7, 45.0, -71.5, 45.0, -69.2, 47.45, -67.8, 47.1, -67.0, 45.7,
  -67.1, 44.8, -69.0, 44.0, -70.7, 43.1, -70.8, 42.3, -70.0, 41.8, -71.4, 41.5,
  -72.9, 41.2, -74.0, 40.7, -74.4, 39.4, -75.0, 38.8, -75.1, 38.0, -76.0, 37.2,
  -76.0, 36.9, -75.5, 35.6, -76.5, 34.7, -78.0, 33.9, -79.2, 33.1, -80.8, 32.1,
  -81.5, 31.1, -81.4, 30.3, -81.3, 29.3, -80.6, 28.3, -80.1, 26.9, -80.1, 25.8,
  -81.1, 25.2, -81.8, 26.0, -82.7, 27.5, -82.7, 28.9, -83.6, 29.9, -84.3, 30.1,
  -85.4, 29.7, -86.5, 30.4, -88.0, 30.3, -89.4, 29.2, -90.2, 29.1, -91.5, 29.5,
  -92.5, 29.6, -93.8, 29.7, -94.8, 29.3, -95.3, 28.9, -96.5, 28.4, -97.4, 27.3,
  -97.2, 26.1, -97.8, 26.0, -99.1, 26.4, -100.0, 28.0, -101.4, 29.8, -102.4,
  29.8, -103.3, 29.0, -104.5, 29.7, -106.5, 31.8, -108.2, 31.3, -111.0, 31.3,
  -114.8, 32.5, -117.1, 32.5, -118.4, 33.7, -120.5, 34.5, -121.9, 36.6, -122.4,
  37.8, -124.0, 40.4, -124.2, 42.0, -124.0, 44.6, -124.1, 46.3,
];

export const US_OUTLINE: Point[] = (() => {
  const out: Point[] = [];
  for (let i = 0; i < OUTLINE_FLAT.length; i += 2) {
    out.push([OUTLINE_FLAT[i] as number, OUTLINE_FLAT[i + 1] as number]);
  }
  return out;
})();

export interface Hub {
  readonly name: string;
  readonly lon: number;
  readonly lat: number;
  readonly home?: boolean;
}

/** Research hubs that light up. San Francisco is the venue everything converges on. */
export const HUBS: readonly Hub[] = [
  { name: "San Francisco", lon: -122.42, lat: 37.77, home: true },
  { name: "Seattle", lon: -122.33, lat: 47.61 },
  { name: "Los Angeles", lon: -118.24, lat: 34.05 },
  { name: "Phoenix", lon: -111.94, lat: 33.43 },
  { name: "Boulder", lon: -105.27, lat: 40.02 },
  { name: "Austin", lon: -97.74, lat: 30.27 },
  { name: "Chicago", lon: -87.63, lat: 41.88 },
  { name: "Madison", lon: -89.4, lat: 43.07 },
  { name: "Urbana", lon: -88.24, lat: 40.11 },
  { name: "Ann Arbor", lon: -83.74, lat: 42.28 },
  { name: "Atlanta", lon: -84.39, lat: 33.75 },
  { name: "Raleigh", lon: -78.64, lat: 35.78 },
  { name: "Pittsburgh", lon: -79.99, lat: 40.44 },
  { name: "New York", lon: -74.01, lat: 40.71 },
  { name: "Boston", lon: -71.11, lat: 42.37 },
];

/*
 * Albers equal-area conic with the standard US parallels. This is the
 * projection that gives the country its familiar curved northern border; an
 * equirectangular one reads as a flat rectangle and stops looking like the US.
 */
const RAD = Math.PI / 180;
const PHI_1 = 29.5 * RAD;
const PHI_2 = 45.5 * RAD;
const LAT_0 = 37.5 * RAD;
const LON_0 = -96 * RAD;
const CONE = (Math.sin(PHI_1) + Math.sin(PHI_2)) / 2;
const CONST_C = Math.cos(PHI_1) ** 2 + 2 * CONE * Math.sin(PHI_1);
const RHO_0 = Math.sqrt(CONST_C - 2 * CONE * Math.sin(LAT_0)) / CONE;

function albers(lon: number, lat: number): Point {
  const lambda = lon * RAD;
  const phi = lat * RAD;
  const rho = Math.sqrt(CONST_C - 2 * CONE * Math.sin(phi)) / CONE;
  const theta = CONE * (lambda - LON_0);
  return [rho * Math.sin(theta), RHO_0 - rho * Math.cos(theta)];
}

function pointInPolygon(x: number, y: number, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i] as Point;
    const [xj, yj] = poly[j] as Point;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export interface MapHub {
  name: string;
  x: number;
  y: number;
  home: boolean;
}

/** A bowed path from an outer hub to the home hub, as a quadratic bezier. */
export interface Route {
  x0: number;
  y0: number;
  cx: number;
  cy: number;
  x1: number;
  y1: number;
}

export interface MapGeometry {
  width: number;
  height: number;
  pitch: number;
  radius: number;
  /** Flat x,y pairs. Kept flat because this is hot in the draw loop. */
  base: Float32Array;
  /** Per-dot opacity multiplier, one per base dot. Absent means every dot is full strength. */
  alpha?: Float32Array;
  hubs: MapHub[];
  routes: Route[];
}

export function buildMapGeometry(width: number, pitch: number): MapGeometry {
  const projected = US_OUTLINE.map(([lon, lat]) => albers(lon, lat));
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of projected) {
    if (x < minX) {
      minX = x;
    }
    if (x > maxX) {
      maxX = x;
    }
    if (y < minY) {
      minY = y;
    }
    if (y > maxY) {
      maxY = y;
    }
  }
  const scale = width / (maxX - minX);
  const height = (maxY - minY) * scale;

  // Albers y grows northward and screen y grows downward, so flip it here.
  const toPx = ([x, y]: Point): Point => [
    (x - minX) * scale,
    (maxY - y) * scale,
  ];
  const poly = projected.map(toPx);

  const dots: number[] = [];
  const rows = Math.floor(height / pitch) + 2;
  const cols = Math.floor(width / pitch) + 2;
  for (let iy = 0; iy < rows; iy++) {
    for (let ix = 0; ix < cols; ix++) {
      // every other row is offset half a cell, which reads denser than a square grid
      const x = ix * pitch + (iy % 2 ? pitch / 2 : 0);
      const y = iy * pitch;
      if (x > width + pitch) {
        continue;
      }
      if (pointInPolygon(x, y, poly)) {
        dots.push(x, y);
      }
    }
  }

  const hubs: MapHub[] = HUBS.map((hub) => {
    const [hx, hy] = toPx(albers(hub.lon, hub.lat));
    // snap onto the nearest lattice dot so a hub reads as part of the field
    let bestX = hx;
    let bestY = hy;
    let bestD = Infinity;
    for (let i = 0; i < dots.length; i += 2) {
      const dx = (dots[i] as number) - hx;
      const dy = (dots[i + 1] as number) - hy;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        bestX = dots[i] as number;
        bestY = dots[i + 1] as number;
      }
    }
    return { name: hub.name, x: bestX, y: bestY, home: hub.home === true };
  });

  const home = hubs.find((h) => h.home);
  const taken = new Set(hubs.map((h) => `${h.x},${h.y}`));
  const base: number[] = [];
  for (let i = 0; i < dots.length; i += 2) {
    if (!taken.has(`${dots[i]},${dots[i + 1]}`)) {
      base.push(dots[i] as number, dots[i + 1] as number);
    }
  }

  // No home hub means nothing to converge on, so there are no routes to draw.
  const routes: Route[] = home
    ? hubs
        .filter((h) => !h.home)
        .map((h) => {
          const dx = home.x - h.x;
          const dy = home.y - h.y;
          const len = Math.hypot(dx, dy) || 1;
          // bow each route perpendicular to its chord so the fan reads as routes, not spokes
          const k = 0.17 * len;
          return {
            x0: h.x,
            y0: h.y,
            cx: (h.x + home.x) / 2 + (-dy / len) * k,
            cy: (h.y + home.y) / 2 + (dx / len) * k,
            x1: home.x,
            y1: home.y,
          };
        })
    : [];

  return {
    width,
    height,
    pitch,
    radius: pitch * 0.215,
    base: Float32Array.from(base),
    hubs,
    routes,
  };
}

/** Point on a quadratic bezier at parameter t. */
export function routePoint(route: Route, t: number): Point {
  const u = 1 - t;
  return [
    u * u * route.x0 + 2 * u * t * route.cx + t * t * route.x1,
    u * u * route.y0 + 2 * u * t * route.cy + t * t * route.y1,
  ];
}
